import ApiService from '@/common/api.service'
import partnerNotesModule from './partnerNotesModule'
import partnerContactsModule from './partnerContactsModule'
import partnerAttachmentsModule from './partnerAttachmentsModule'
import partnerLocationsModule from './partnerLocationsModule'
import store from '@/store/index'

import * as utils from '@/common/utils'
import * as notesHelpers from '@/store/notes/notes.helpers'
import * as contactsHelpers from '@/store/contacts/contacts.helpers'
import * as attachmentsHelpers from '@/store/attachments/attachments.helpers'
import * as locationsHelpers from '@/store/locations/locations.helpers'

const module = 'admin/partnersModule'
const notesModule = module + '/notes'
const contactsModule = module + '/contacts'
const attachmentsModule = module + '/attachments'
const locationsModule = module + '/locations'

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    id: null,
    name: '',
    fin_account_no: '',
    description: '',
    report_logo_url: '',
    report_left_header: '',
    report_right_header: '',
    created_at: '',
    updated_at: '',
    note: ''
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param partner     Object
 * @param notes       Array|null
 * @param contacts    Array|null
 * @param attachments Array|null
 * @param locations   Array|null
 *
 * @return Object
 */
function transformCreate (partner, notes = null, contacts = null, attachments = null, locations = null) {
  return {
    id: partner.id,
    name: partner.name,
    fin_account_no: partner.fin_account_no,
    description: partner.description,
    report_logo_url: partner.report_logo_url,
    report_left_header: partner.report_left_header,
    report_right_header: partner.report_right_header,
    notes,
    contacts,
    attachments,
    locations
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param partner     Object
 *
 * @return Object
 */
function transformUpdate (partner) {
  return {
    id: partner.id,
    name: partner.name,
    fin_account_no: partner.fin_account_no,
    description: partner.description,
    report_logo_url: partner.report_logo_url,
    report_left_header: partner.report_left_header,
    report_right_header: partner.report_right_header
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  original: {
    name: '',
    fin_account_no: '',
    description: '',
    report_logo_url: '',
    report_left_header: '',
    report_right_header: ''
  }
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return (state.data.name !== state.original.name) ||
      ((state.data.fin_account_no || null) !== state.original.fin_account_no) ||
      ((state.data.description || null) !== state.original.description) ||
      ((state.data.report_logo_url || null) !== state.original.report_logo_url) ||
      ((state.data.report_left_header || null) !== state.original.report_left_header) ||
      ((state.data.report_right_header || null) !== state.original.report_right_header)
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`partners/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        contactsHelpers.setContacts(contactsModule, response.data.data.contacts)
        contactsHelpers.setContactTypes(contactsModule, response.data.data.contact_types)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        locationsHelpers.setLocations(locationsModule, response.data.data.locations)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`partners/create`)
      .then((response) => {
        context.commit('set', defaults())
        notesHelpers.purge(notesModule)
        contactsHelpers.purge(contactsModule)
        contactsHelpers.setContactTypes(contactsModule, response.data.data.contact_types)
        attachmentsHelpers.purge(attachmentsModule)
        locationsHelpers.purge(locationsModule)
        locationsHelpers.setLocationTypes(locationsModule, response.data.data.location_types)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`partners/${id}/edit/`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setOriginal', response.data.data)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        contactsHelpers.setContacts(contactsModule, response.data.data.contacts)
        contactsHelpers.setContactTypes(contactsModule, response.data.data.contact_types)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        locationsHelpers.setLocations(locationsModule, response.data.data.locations)
        locationsHelpers.setLocationTypes(locationsModule, response.data.data.location_types)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let notes = []
    let note = context.getters['get'].note || null
    if (note !== null && note.trim() !== '') {
      notes.push(note)
    }

    let contacts = contactsHelpers.contacts(contactsModule)
    let attachments = attachmentsHelpers.attachments(attachmentsModule)
    let locations = locationsHelpers.locations(locationsModule)

    return ApiService
      .post(`partners`, transformCreate(
        context.getters['get'], notes, contacts, attachments, locations
      ))
      .then((response) => {
        notesHelpers.purge(notesModule)
        contactsHelpers.purgeContacts(contactsModule)
        attachmentsHelpers.purge(attachmentsModule)
        locationsHelpers.purgeLocations(locationsModule)
        context.commit('set', defaults())
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    let partner = context.getters['get']

    return ApiService
      .put(`partners/${partner.id}`, transformUpdate(partner))
      .then((response) => {
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    let id = context.getters['get'].id
    return ApiService
      .delete(`partners/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`partners`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = {
      id: data.id,
      name: data.name,
      fin_account_no: data.fin_account_no,
      description: data.description,
      report_logo_url: data.report_logo_url,
      report_left_header: data.report_left_header,
      report_right_header: data.report_right_header,
      created_at: data.created_at,
      updated_at: data.updated_at,
      notes: data.notes
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      name: data.name,
      fin_account_no: data.fin_account_no,
      description: data.description
    }
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.data[name].push(value)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original.name = ''
    state.original.fin_account_no = ''
    state.original.description = ''
    state.original.report_logo_url = ''
    state.original.report_left_header = ''
    state.original.report_right_header = ''
    store.commit('admin/partnersModule/attachments/purge')
    store.commit('admin/partnersModule/attachments/resetFormState')
    store.commit('admin/partnersModule/contacts/purge')
    store.commit('admin/partnersModule/contacts/resetFormState')
    store.commit('admin/partnersModule/locations/purge')
    store.commit('admin/partnersModule/locations/resetFormState')
    store.commit('admin/partnersModule/notes/purge')
    store.commit('admin/partnersModule/notes/resetFormState')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    notes: partnerNotesModule,
    contacts: partnerContactsModule,
    attachments: partnerAttachmentsModule,
    locations: partnerLocationsModule
  }
}



// WEBPACK FOOTER //
// ./src/store/administration/partners/partnersModule.js